import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaTimes, FaEnvelope } from 'react-icons/fa';
import { AppDispatch, RootState } from '../../redux/Store';
import { fetchMessageThreads, MessageThread } from '../../redux/thunks/messagesThunks';
import defaultAvatar from '../../images/default_avatar.png';
import { getMediaUrl } from '@/constants/media';
import { formatRelativeTime } from '@/utils/formatTime';
import './MessagesPanel.scss';

interface Message {
  id: string;
  from: {
    name: string;
    avatar?: string;
    username: string;
  };
  lastMessage: string;
  timestamp: Date;
  unread: boolean;
}

interface MessagesPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MessagesPanel: React.FC<MessagesPanelProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  // Read threads from Redux directly — same selector the AppBar badge
  // uses — so the panel and badge can never disagree. Previously the
  // panel kept a parallel useState that got out of sync when the fetch
  // failed silently: Redux retained socket-bumped unread state (badge
  // stayed at 1), the panel's local state stayed empty (panel showed
  // "No messages").
  const threads = useSelector((state: RootState) => state.messages.threads);
  const isLoading = useSelector((state: RootState) => state.messages.loading.threads);

  // Fetch when the panel opens so we get fresh data, but the rendered
  // list comes from Redux — fetch failures don't blank the panel.
  useEffect(() => {
    if (isOpen && user) {
      dispatch(fetchMessageThreads());
    }
  }, [isOpen, user, dispatch]);

  const messages = useMemo<Message[]>(() => {
    return threads.map((thread: MessageThread) => {
      const otherParticipant =
        thread.participants.find(p => p.id !== user?.id) || thread.participants[0];
      return {
        id: thread.id,
        from: {
          name: otherParticipant.name,
          username: otherParticipant.username,
          avatar: otherParticipant.avatar,
        },
        lastMessage: thread.lastMessage.content,
        timestamp: new Date(thread.lastMessage.timestamp),
        unread: thread.unreadCount > 0,
      };
    });
  }, [threads, user?.id]);

  const handleMessageClick = (messageId: string) => {
    navigate(`/messages?thread=${messageId}`);
    onClose();
  };

  const handleViewAll = () => {
    navigate('/messages');
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="messages-overlay" onClick={onClose} />}

      {/* Side Panel */}
      <div className={`messages-panel ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="messages-header">
          <h2 className="messages-title">Messages</h2>
          <button
            className="messages-close-btn"
            onClick={onClose}
            aria-label="Close messages"
          >
            <FaTimes />
          </button>
        </div>

        {/* Messages List */}
        <div className="messages-list">
          {isLoading ? (
            <div className="messages-empty">
              <div className="messages-spinner" />
              <p>Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="messages-empty">
              <FaEnvelope />
              <p>No messages</p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`message-item ${message.unread ? 'unread' : ''}`}
                onClick={() => handleMessageClick(message.id)}
              >
                <div className="message-avatar">
                  <img src={getMediaUrl(message.from.avatar || null) || defaultAvatar} alt={message.from.name} />
                </div>

                <div className="message-content">
                  <div className="message-header-row">
                    <span className="message-from-name">{message.from.name}</span>
                    <span className="message-timestamp">
                      {formatRelativeTime(message.timestamp)}
                    </span>
                  </div>
                  <p className="message-text">{message.lastMessage}</p>
                </div>

                {message.unread && <div className="message-unread-indicator" />}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="messages-footer">
          <button className="messages-view-all-btn" onClick={handleViewAll}>
            View All Messages
          </button>
        </div>
      </div>
    </>
  );
};

export default MessagesPanel;
